/**
 * Adegen Custom Header Component
 * Tracks header height via ResizeObserver for --header-height CSS variable
 * Sticky positioning handled by CSS on the section wrapper
 */
class AdegenHeaderComponent extends HTMLElement {
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;
  /** @type {MediaQueryList|null} */
  #desktopMedia = null;
  /** @type {(event: MouseEvent) => void} */
  #outsideClickHandler = () => {};

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry?.borderBoxSize?.[0]) return;
      const height = Math.round(entry.borderBoxSize[0].blockSize);
      document.body.style.setProperty('--header-height', `${height}px`);

      // Update header-group height (includes announcement bar, etc.)
      const headerGroup = document.getElementById('header-group');
      if (headerGroup) {
        let groupHeight = 0;
        for (const child of headerGroup.children) {
          if (child instanceof HTMLElement) {
            groupHeight += child.offsetHeight;
          }
        }
        document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
      }
    });
    this.#resizeObserver.observe(this);

    this.#desktopMedia = window.matchMedia('(min-width: 900px)');
    this.#setupMegaMenu();
    this.#setupDrawer();
    this.#setupEditorEvents();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#desktopMedia = null;
    document.removeEventListener('click', this.#outsideClickHandler);
  }

  #setupMegaMenu() {
    const megaWrapper = this.querySelector('[data-mega-wrapper]');
    const triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    const panels = [...this.querySelectorAll('[data-mega-panel]')];

    if (!megaWrapper || triggers.length === 0 || panels.length === 0) return;

    const openPanel = (id) => {
      if (!this.#desktopMedia?.matches) return;
      panels.forEach((panel) => {
        panel.classList.toggle('is-open', panel.dataset.megaPanel === id);
      });
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', trigger.dataset.megaId === id ? 'true' : 'false');
      });
    };

    const closePanels = () => {
      panels.forEach((panel) => panel.classList.remove('is-open'));
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    };

    const isAnyOpen = () => panels.some((panel) => panel.classList.contains('is-open'));

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (!this.#desktopMedia?.matches) return;
        event.preventDefault();
        const id = trigger.dataset.megaId;
        const alreadyOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (alreadyOpen) {
          closePanels();
        } else {
          openPanel(id);
        }
      });
    });

    this.#outsideClickHandler = (event) => {
      if (!isAnyOpen()) return;
      const target = event.target;
      if (target instanceof Node && (this.contains(target) || megaWrapper.contains(target))) return;
      closePanels();
    };
    document.addEventListener('click', this.#outsideClickHandler);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanels();
    });

    this.#desktopMedia.addEventListener('change', () => {
      closePanels();
    });
  }

  #setupDrawer() {
    const hamburger = this.querySelector('.adegen-header__hamburger');
    const overlay = this.querySelector('[data-drawer-overlay]');
    const viewport = this.querySelector('.adegen-header__drawer-viewport');

    if (!hamburger) return;

    const setOpen = (open) => {
      this.dataset.drawerOpen = open ? 'true' : 'false';
      document.body.classList.toggle('adegen-header-drawer-open', open);
      hamburger.setAttribute('aria-expanded', open ? 'true' : 'false');

      // Reset to root panel when closing
      if (!open && viewport) {
        viewport.classList.remove('is-mega-active');
        this.querySelectorAll('[data-drawer-mega-panel]').forEach((panel) => {
          panel.classList.remove('is-active');
        });
      }
    };

    hamburger.addEventListener('click', () => {
      const isOpen = this.dataset.drawerOpen === 'true';
      setOpen(!isOpen);
    });

    overlay?.addEventListener('click', () => setOpen(false));

    // Mega panel openers (from root menu)
    this.querySelectorAll('[data-drawer-mega]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.getAttribute('data-drawer-mega');
        const panel = this.querySelector(`[data-drawer-mega-panel="${panelId}"]`);
        if (!panel || !viewport) return;

        // Deactivate all mega panels first
        this.querySelectorAll('[data-drawer-mega-panel]').forEach((p) => {
          p.classList.remove('is-active');
        });

        // Activate the target panel and tell viewport to slide
        viewport.classList.add('is-mega-active');
        panel.classList.add('is-active');
      });
    });

    // Back buttons (return to root)
    this.querySelectorAll('[data-drawer-back]').forEach((backBtn) => {
      backBtn.addEventListener('click', () => {
        if (!viewport) return;
        viewport.classList.remove('is-mega-active');
        this.querySelectorAll('[data-drawer-mega-panel]').forEach((panel) => {
          panel.classList.remove('is-active');
        });
      });
    });

    this.#desktopMedia?.addEventListener('change', (event) => {
      if (event.matches) setOpen(false);
    });
  }

  /* ── Theme Editor: show mega panel when block is selected ── */
  #setupEditorEvents() {
    if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

    this.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail.blockId;
      const panel = this.querySelector(`[data-mega-panel="${blockId}"]`);
      if (!panel) return;

      /* close any other panels first */
      this.querySelectorAll('[data-mega-panel]').forEach((p) => {
        p.classList.remove('is-open', 'is-editor-selected');
      });

      panel.classList.add('is-open', 'is-editor-selected');
    });

    this.addEventListener('shopify:block:deselect', (event) => {
      const blockId = event.detail.blockId;
      const panel = this.querySelector(`[data-mega-panel="${blockId}"]`);
      if (!panel) return;

      panel.classList.remove('is-open', 'is-editor-selected');
    });
  }
}

if (!customElements.get('adegen-header-component')) {
  customElements.define('adegen-header-component', AdegenHeaderComponent);
}
